import React, { useState, useEffect } from 'react';
import styles from './DeviceEdit.module.css';

const lightStatusOptions = [
    { value: 'off', label: 'Off' },
    { value: 'on', label: 'On' },
];
const signalStatusOptions = [
    { value: 'red', label: 'Red' },
    { value: 'green', label: 'Green' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'blinking', label: 'Blinking' }
];

const directionIconOptions = [
  { value: 'up', label: 'Up', img: '/static/icon-direction/up.svg' },
  { value: 'down', label: 'Down', img: '/static/icon-direction/down.svg' },
  { value: 'right', label: 'Right', img: '/static/icon-direction/right.svg' },
  { value: 'left', label: 'Left', img: '/static/icon-direction/left.svg' },
  { value: 'left,reversed', label: 'Left-Reversed', img: '/static/icon-direction/left-reverser.svg' },
  { value: 'right,reversed', label: 'Right-Reversed', img: '/static/icon-direction/right-reverser.svg' },
  { value: 'up,right', label: 'Up-Right', img: '/static/icon-direction/up-right.svg' },
  { value: 'up,left', label: 'Up-Left', img: '/static/icon-direction/up-left.svg' },
  { value: 'down,right', label: 'Down-Right', img: '/static/icon-direction/down-right.svg' },
  { value: 'down,left', label: 'Down-Left', img: '/static/icon-direction/down-left.svg' },
];

const DeviceEdit = ({ 
    layout,
    device, 
    isOpen, 
    onClose, 
    onSave, 
    onDelete 
}) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'signal',
        position: { x: 0, y: 0 },
        status: 'off',
        layoutId: layout._id,
        directionDisplay: ['up'],
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (device) {
            const baseFormData = {
                name: device.name || '',
                type: device.type || 'signal',
                position: device.position || { x: 0, y: 0 },
                status: device.status || 'off',
                layoutId: device.layoutId || layout._id,
                directionDisplay: Array.isArray(device.directionDisplay)
                    ? device.directionDisplay
                    : (device.directionDisplay
                    ? String(device.directionDisplay).split(',') // nếu là string → split
                    : ['up']),
            };
            if (device.type === 'light') {
                baseFormData.brightness = device.brightness || 0;
            }

            setFormData(baseFormData);
        } else {
            // Reset form for new device
            setFormData({
                name: '',
                type: 'signal',
                position: { x: 0, y: 0 },
                status: 'off',
                layoutId: layout._id,
                directionDisplay: ['up'],
            });
        }
    }, [device, layout]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Device name is required';
        }
        
        if (!formData.type) {
            newErrors.type = 'Device type is required';
        }
        
        if (formData.position.x < 0 || formData.position.x > layout.size?.width) {
            newErrors.positionX = 'X position must be correct positive';
        }

        if (formData.position.y < 0 || formData.position.y > layout.size?.height) {
            newErrors.positionY = 'Y position must be correct positive';
        }
        
        if (formData.type === 'light' && (formData.brightness < 0 || formData.brightness > 100)) {
            newErrors.brightness = 'Brightness must be between 0 and 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            await onSave({
                ...device,
                ...formData
            });
            onClose();
        } catch (error) {
            console.error('Error saving device:', error);
            setErrors({ submit: 'Failed to save device. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this device?')) {
            setIsLoading(true);
            try {
                await onDelete(device._id);
                onClose();
            } catch (error) {
                console.error('Error deleting device:', error);
                setErrors({ submit: 'Failed to delete device. Please try again.' });
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handlePositionChange = (axis, value) => {
        setFormData(prev => ({
            ...prev,
            position: {
                ...prev.position,
                [axis]: parseFloat(value) || 0
            }
        }));
        
        // Clear position errors
        const errorField = `position${axis.toUpperCase()}`;
        if (errors[errorField]) {
            setErrors(prev => ({
                ...prev,
                [errorField]: null
            }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        {device?._id ? 'Edit Device' : 'Add Device'}
                    </h2>
                    <button 
                        className={styles.closeButton}
                        onClick={onClose}
                        type="button"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.formBody}>
                        {/* Device Name */}
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Device Name *</label>
                            <input
                                type="text"
                                className={`${styles.input} ${errors.name ? styles.error : ''}`}
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter device name"
                                disabled={isLoading}
                            />
                            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                        </div>

                        {/* Device Type */}
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Device Type *</label>
                            <select
                                className={`${styles.select} ${errors.type ? styles.error : ''}`}
                                value={formData.type}
                                onChange={(e) => handleInputChange('type', e.target.value)}
                                disabled={isLoading}
                            >
                                <option value="light">Light</option>
                                <option value="signal">Signal</option>
                            </select>
                            {errors.type && <span className={styles.errorText}>{errors.type}</span>}
                        </div>

                        {/* Direction/Icon for Signal */}
                        <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: 8, // khoảng cách giữa các ô
                        }}
                        >
                        {formData.type === 'signal' && directionIconOptions.map((opt) => {
                            const currentValue = Array.isArray(formData.directionDisplay)
                            ? formData.directionDisplay.join(',')
                            : String(formData.directionDisplay || '');

                            return (
                            <label
                                key={opt.value}
                                title={opt.label}
                                style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                border:
                                    currentValue === opt.value
                                    ? '1px solid #4caf50'
                                    : '1px solid #ddd',
                                borderRadius: 6,
                                padding: '6px 10px',
                                cursor: 'pointer',
                                }}
                            >
                                <input
                                type="radio"
                                name="directionDisplay"
                                value={opt.value}
                                checked={currentValue === opt.value}
                                onChange={(e) =>
                                    handleInputChange('directionDisplay', e.target.value.split(','))
                                }
                                disabled={isLoading}
                                />
                                <img
                                src={opt.img}
                                width="20"
                                height="20"
                                alt={opt.label}
                                style={{
                                    backgroundColor:
                                    currentValue === opt.value ? '#4caf50' : '#ddd',
                                }}
                                />
                            </label>
                            );
                        })}
                        </div>

                        {/* Position */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputGroup}>
                                <label className={styles.label}>Position X *</label>
                                <input
                                    type="number"
                                    className={`${styles.input} ${errors.positionX ? styles.error : ''}`}
                                    value={formData.position.x}
                                    onChange={(e) => handlePositionChange('x', e.target.value)}
                                    placeholder="X coordinate"
                                    min="0"
                                    disabled={isLoading}
                                />
                                {errors.positionX && <span className={styles.errorText}>{errors.positionX}</span>}
                            </div>
                            <div className={styles.inputGroup}>
                                <label className={styles.label}>Position Y *</label>
                                <input
                                    type="number"
                                    className={`${styles.input} ${errors.positionY ? styles.error : ''}`}
                                    value={formData.position.y}
                                    onChange={(e) => handlePositionChange('y', e.target.value)}
                                    placeholder="Y coordinate"
                                    min="0"
                                    disabled={isLoading}
                                />
                                {errors.positionY && <span className={styles.errorText}>{errors.positionY}</span>}
                            </div>
                        </div>

                        {/* Status */}
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Status</label>
                            <select
                                className={styles.select}
                                value={formData.status}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                disabled={isLoading}
                            >
                                {formData.type === 'light' ? (
                                lightStatusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                    {option.label}
                                    </option>
                                ))
                                ) : formData.type === 'signal' ? (
                                signalStatusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                    {option.label}
                                    </option>
                                ))
                                ) : null}
                            </select>
                        </div>

                        {/* Brightness (only for lights) */}
                        {formData.type === 'light' && (
                            <div className={styles.inputGroup}>
                                <label className={styles.label}>
                                    Brightness: {formData.brightness}%
                                </label>
                                <div className={styles.brightnessContainer}>
                                    <input
                                        type="range"
                                        className={styles.slider}
                                        min="0"
                                        max="100"
                                        step="5"
                                        value={formData.brightness}
                                        onChange={(e) => handleInputChange('brightness', parseInt(e.target.value))}
                                        disabled={isLoading}
                                    />
                                    <div className={styles.brightnessLabels}>
                                        <span>0%</span>
                                        <span>50%</span>
                                        <span>100%</span>
                                    </div>
                                </div>
                                {errors.brightness && <span className={styles.errorText}>{errors.brightness}</span>}
                            </div>
                        )}

                        {/* Submit Error */}
                        {errors.submit && (
                            <div className={styles.submitError}>
                                {errors.submit}
                            </div>
                        )}
                    </div>

                    <div className={styles.modalFooter}>
                        {device?._id && (
                            <button
                                type="button"
                                className={styles.deleteButton}
                                onClick={handleDelete}
                                disabled={isLoading}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3,6 5,6 21,6"/>
                                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
                                </svg>
                                Delete
                            </button>
                        )}
                        <div className={styles.actionButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={onClose}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className={styles.loading}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-6.219-8.56"/>
                                        </svg>
                                        Saving...
                                    </span>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                                            <polyline points="17,21 17,13 7,13 7,21"/>
                                            <polyline points="7,3 7,8 15,8"/>
                                        </svg>
                                        {device?._id ? 'Update' : 'Create'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DeviceEdit; 